import { useMemo } from 'react';
import {
  checkProjectionIdentities,
  IdentityViolation,
} from '@shared/calculations/funding/projection/assertProjectionIdentities';
import { CollapsibleSection } from './CollapsibleSection';

interface IdentityChecksPanelProps {
  projection: ClaimProjection;
}

interface MonthViolations {
  monthArrayIndex: number;
  monthIndex: number;
  violations: IdentityViolation[];
}

export function IdentityChecksPanel({ projection }: IdentityChecksPanelProps) {
  const allViolations = useMemo<MonthViolations[]>(() => {
    const out: MonthViolations[] = [];
    projection.months.forEach((month, i) => {
      const violations = checkProjectionIdentities(month);
      if (violations.length > 0) {
        out.push({ monthArrayIndex: i, monthIndex: month.context.monthIndex, violations });
      }
    });
    return out;
  }, [projection]);

  const totalMonths = projection.months.length;
  const failingMonths = allViolations.length;
  const allPass = failingMonths === 0;

  const subtitle = allPass ? (
    <span className="text-green-600">● All {totalMonths} months pass</span>
  ) : (
    <span className="text-red-600">
      ✗ {failingMonths} of {totalMonths} months failed
    </span>
  );

  return (
    <CollapsibleSection title="Identity checks" subtitle={subtitle} defaultOpen={false}>
      {allPass ? (
        <p className="text-sm text-gray-500">
          All internal accounting identities hold for every month in this projection.
        </p>
      ) : (
        <div className="space-y-3">
          {allViolations.map(({ monthArrayIndex, monthIndex, violations }) => (
            <div key={monthArrayIndex} className="rounded border border-red-200 bg-red-50 p-3">
              <div className="text-sm font-medium text-red-800">
                Month {monthArrayIndex} (monthIndex {monthIndex})
              </div>
              <ul className="mt-2 space-y-1 text-xs text-gray-700">
                {violations.map((v, vi) => (
                  <li key={vi} className="font-mono">
                    <span className="font-semibold">{v.balance}</span>: open={v.open.toFixed(2)},
                    expected={v.expectedClose.toFixed(2)}, actual={v.close.toFixed(2)},{' '}
                    <span className="text-red-700">Δ={v.delta.toFixed(4)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
